import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, Mail, Trash2, Eye, Archive, Settings, Save, RefreshCw, Check, FolderArchive } from 'lucide-react';
import { toast } from 'sonner';
import { inquiriesApi, settingsApi } from '@/lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useAdminLanguage } from '@/context/AdminLanguageContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function AdminInquiries() {
  const { t } = useAdminLanguage();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);
  const [archiveId, setArchiveId] = useState(null);
  const [activeTab, setActiveTab] = useState('inquiries');
  
  // Auto-reply settings
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    loadInquiries();
    loadSettings();
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const loadInquiries = async () => {
    try {
      const response = await inquiriesApi.getAll();
      setInquiries(response.data);
    } catch (error) {
      console.error('Error loading inquiries:', error);
      toast.error('Fehler beim Laden der Anfragen');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await inquiriesApi.delete(deleteId);
      setInquiries(inquiries.filter(inq => inq.id !== deleteId));
      toast.success('Anfrage erfolgreich gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen der Anfrage');
    } finally {
      setDeleteId(null);
    }
  };

  const handleArchive = async () => {
    if (!archiveId) return;
    try {
      await inquiriesApi.archiveInquiry(archiveId);
      setInquiries(inquiries.filter(inq => inq.id !== archiveId));
      toast.success('Anfrage archiviert');
    } catch (error) {
      toast.error('Fehler beim Archivieren');
    } finally {
      setArchiveId(null);
    }
  };

  // Auto-save settings
  const autoSave = async (newSettings) => {
    setSaving(true);
    try {
      await settingsApi.update(newSettings);
      setAutoSaved(true);
      setTimeout(() => setAutoSaved(false), 2000);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      autoSave(newSettings);
    }, 1000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredInquiries = inquiries.filter(inq => {
    const matchesSearch = 
      inq.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inq.customer_email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || inq.status === filter;
    return matchesSearch && matchesFilter;
  });

  const newCount = inquiries.filter(i => i.status === 'new').length;
  const repliedCount = inquiries.filter(i => i.status === 'replied').length;

  return (
    <div data-testid="admin-inquiries">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="font-heading text-3xl font-light">{t('inquiries_title')}</h1>
          <p className="text-[#666666] mt-1">{t('inquiries_title') === 'Inquiries' ? 'Manage customer messages' : 'Verwalten Sie Kundennachrichten'}</p>
        </div>
        <Link
          to="/admin/inquiries/archive"
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] rounded-lg hover:bg-[#F9F9F7] transition-colors text-sm"
        >
          <Archive size={16} />
          {t('inquiries_archived')}
        </Link>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#F9F9F7] p-1 rounded-lg">
          <TabsTrigger value="inquiries" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Mail size={16} />
            {t('inquiries_tab_messages')}
            {newCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-gold text-white rounded-full">{newCount}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Settings size={16} />
            {t('inquiries_tab_settings')}
          </TabsTrigger>
        </TabsList>

        {/* Inquiries Tab */}
        <TabsContent value="inquiries" className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <button
              onClick={() => setFilter('all')}
              className={`bg-white border p-4 text-left transition-colors ${
                filter === 'all' ? 'border-gold' : 'border-[#E5E5E5]'
              }`}
            >
              <p className="overline mb-1">{t('inquiries_all')}</p>
              <p className="font-heading text-2xl">{inquiries.length}</p>
            </button>
            <button
              onClick={() => setFilter('new')}
              className={`bg-white border p-4 text-left transition-colors ${
                filter === 'new' ? 'border-gold' : 'border-[#E5E5E5]'
              }`}
            >
              <p className="overline mb-1">{t('inquiries_unread')}</p>
              <p className="font-heading text-2xl text-gold">{newCount}</p>
            </button>
            <button
              onClick={() => setFilter('replied')}
              className={`bg-white border p-4 text-left transition-colors ${
                filter === 'replied' ? 'border-gold' : 'border-[#E5E5E5]'
              }`}
            >
              <p className="overline mb-1">{t('inquiries_reply') === 'Reply' ? 'Replied' : 'Beantwortet'}</p>
              <p className="font-heading text-2xl text-green-600">{repliedCount}</p>
            </button>
          </div>

          {/* Search */}
          <div className="bg-white border border-[#E5E5E5] p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999]" size={18} />
              <input
                type="text"
                placeholder={t('inquiries_title') === 'Inquiries' ? 'Search by name or email...' : 'Suche nach Name oder E-Mail...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none"
                data-testid="inquiries-search"
              />
            </div>
          </div>

          {/* Inquiries List */}
          <div className="bg-white border border-[#E5E5E5]">
            {loading ? (
              <div className="p-8 text-center">
                <div className="spinner mx-auto" />
              </div>
            ) : filteredInquiries.length === 0 ? (
              <div className="p-8 text-center">
                <Mail size={48} className="mx-auto text-[#E5E5E5] mb-4" />
                <p className="text-[#666666]">{t('inquiries_no_results')}</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E5E5E5]">
                {filteredInquiries.map((inquiry) => (
                  <div key={inquiry.id} className="p-4 hover:bg-[#F9F9F7] transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium truncate">{inquiry.customer_name}</h3>
                          <span className={inquiry.status === 'new' ? 'status-new' : 'status-replied'}>
                            {inquiry.status === 'new' ? t('inquiries_unread') : (t('inquiries_reply') === 'Reply' ? 'Replied' : 'Beantwortet')}
                          </span>
                        </div>
                        <p className="text-sm text-[#666666] truncate">{inquiry.customer_email}</p>
                        <p className="text-sm text-[#999999] mt-1 line-clamp-2">{inquiry.message}</p>
                        <p className="text-xs text-[#999999] mt-2">{formatDate(inquiry.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Link
                          to={`/admin/inquiries/${inquiry.id}`}
                          className="p-2 hover:bg-white rounded border border-transparent hover:border-[#E5E5E5]"
                          title={t('inquiries_title') === 'Inquiries' ? 'View details' : 'Details anzeigen'}
                        >
                          <Eye size={18} />
                        </Link>
                        <button
                          onClick={() => setArchiveId(inquiry.id)}
                          className="p-2 hover:bg-amber-50 text-amber-600 rounded"
                          title={t('inquiries_archive')}
                        >
                          <FolderArchive size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteId(inquiry.id)}
                          className="p-2 hover:bg-red-50 text-red-500 rounded"
                          title={t('inquiries_delete')}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Auto-Reply Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          {settingsLoading ? (
            <div className="p-8 text-center">
              <div className="spinner mx-auto" />
            </div>
          ) : settings ? (
            <>
              {/* Save Status */}
              <div className="flex items-center justify-end gap-2">
                {saving && (
                  <span className="text-sm text-[#666666] flex items-center gap-2">
                    <RefreshCw size={16} className="animate-spin" />
                    {t('msg_loading') || 'Speichern...'}
                  </span>
                )}
                {autoSaved && !saving && (
                  <span className="text-sm text-green-600 flex items-center gap-2">
                    <Check size={16} />
                    {t('msg_save_success')}
                  </span>
                )}
              </div>

              {/* Auto-Reply Toggle */}
              <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-lg">{t('inquiries_autoreply_title')}</h3>
                    <p className="text-sm text-[#666666] mt-1">
                      {t('inquiries_autoreply_desc')}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.auto_reply_enabled || false}
                      onChange={(e) => handleSettingChange('auto_reply_enabled', e.target.checked)}
                      className="sr-only peer"
                      data-testid="auto-reply-toggle"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
                    <span className="ml-3 text-sm font-medium">
                      {settings.auto_reply_enabled ? t('inquiries_autoreply_activate') : (t('inquiries_title') === 'Inquiries' ? 'Inactive' : 'Inaktiv')}
                    </span>
                  </label>
                </div>

                {settings.auto_reply_enabled && (
                  <div className="pt-4 border-t border-[#E5E5E5] space-y-4">
                    <div>
                      <label className="overline block mb-2">{t('inquiries_autoreply_text')} (Deutsch)</label>
                      <textarea
                        value={settings.auto_reply_message || ''}
                        onChange={(e) => handleSettingChange('auto_reply_message', e.target.value)}
                        className="input-premium w-full h-32 resize-none"
                        placeholder={t('inquiries_autoreply_placeholder')}
                        data-testid="auto-reply-message"
                      />
                    </div>
                    <div>
                      <label className="overline block mb-2">{t('inquiries_autoreply_text')} (English)</label>
                      <textarea
                        value={settings.auto_reply_message_en || ''}
                        onChange={(e) => handleSettingChange('auto_reply_message_en', e.target.value)}
                        className="input-premium w-full h-32 resize-none"
                        placeholder={t('inquiries_title') === 'Inquiries' ? 'Thank you for your message! We will get back to you as soon as possible.' : 'Thank you for your message! We will get back to you as soon as possible.'}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>💡 {t('inquiries_title') === 'Inquiries' ? 'Note:' : 'Hinweis:'}</strong> {t('inquiries_title') === 'Inquiries' ? 'When enabled, customers automatically receive a confirmation message after submitting an inquiry. The message is sent in the customer\'s language (based on the website language).' : 'Wenn aktiviert, erhält der Kunde automatisch eine Bestätigungsnachricht nach dem Absenden einer Anfrage. Die Nachricht wird in der Sprache des Kunden gesendet (basierend auf der Website-Sprache).'}
                </p>
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-[#666666]">
              {t('msg_no_data') || 'Einstellungen konnten nicht geladen werden.'}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('inquiries_delete') === 'Delete' ? 'Delete Inquiry' : 'Anfrage löschen'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('inquiries_delete') === 'Delete' ? 'Are you sure you want to delete this inquiry? This action cannot be undone.' : 'Sind Sie sicher, dass Sie diese Anfrage löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('action_cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('action_delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Confirmation */}
      <AlertDialog open={!!archiveId} onOpenChange={() => setArchiveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('inquiries_archive') === 'Archive' ? 'Archive Inquiry' : 'Anfrage archivieren'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('inquiries_archive') === 'Archive' ? 'This inquiry will be moved to the archive and will no longer appear in the main list. You can view archived inquiries in the archive section at any time.' : 'Diese Anfrage wird ins Archiv verschoben und erscheint nicht mehr in der Hauptliste. Sie können archivierte Anfragen jederzeit im Archiv einsehen.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('action_cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} className="bg-amber-600 hover:bg-amber-700">
              {t('inquiries_archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
